<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import sipCallAdapter from '../adapters/sipCallAdapter';
import sipConnectionManager from '../managers/SipConnectionManager';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const calls = ref([]);
const now = ref(Date.now());
const pendingNavigationCallSid = ref(null);

let durationTimer = null;

const activeCall = computed(
  () => calls.value.find(call => call.isActive) || null
);
const incomingCalls = computed(() =>
  calls.value.filter(call => !call.isActive)
);
const primaryCall = computed(
  () => activeCall.value || incomingCalls.value[0] || null
);
const hasActiveCall = computed(() => Boolean(activeCall.value));
const hasCalls = computed(() => calls.value.length > 0);

const startDurationTimer = () => {
  if (durationTimer) {
    window.clearInterval(durationTimer);
  }

  durationTimer = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
};

const stopDurationTimer = () => {
  if (durationTimer) {
    window.clearInterval(durationTimer);
    durationTimer = null;
  }
};

watch(
  () => activeCall.value?.connectedAt,
  connectedAt => {
    if (connectedAt) {
      now.value = Date.now();
      startDurationTimer();
    } else {
      stopDurationTimer();
    }
  },
  { immediate: true }
);

const formatDuration = connectedAt => {
  if (!connectedAt) {
    return null;
  }

  const elapsedSeconds = Math.max(
    0,
    Math.floor((now.value - connectedAt) / 1000)
  );
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

const getCallInfo = call => {
  const conversation = call?.conversationId
    ? store.getters.getConversationById(call.conversationId)
    : null;
  const inbox = store.getters['inboxes/getInbox'](
    conversation?.inbox_id || call?.inboxId
  );
  const sender = conversation?.meta?.sender;

  return {
    conversation,
    inbox,
    contactName:
      sender?.name ||
      sender?.phone_number ||
      call?.callerName ||
      call?.callerId ||
      call?.calledNumber ||
      'Unknown caller',
    inboxName: inbox?.name || 'SIP',
    avatar: sender?.avatar || sender?.thumbnail,
  };
};

const navigateToConversation = (conversationId, inboxId = null) => {
  const accountId = route.params.accountId;
  if (!conversationId || !accountId) {
    return;
  }

  router.push({
    path: frontendURL(
      conversationUrl({
        accountId,
        activeInbox: inboxId,
        id: conversationId,
      })
    ),
  });
};

const maybeNavigatePendingCall = call => {
  if (
    pendingNavigationCallSid.value &&
    call?.callSid === pendingNavigationCallSid.value &&
    call?.conversationId
  ) {
    pendingNavigationCallSid.value = null;
    navigateToConversation(call.conversationId, call.inboxId);
  }
};

const upsertCall = callData => {
  if (!callData?.callSid) {
    return;
  }

  const existingCall = calls.value.find(
    call => call.callSid === callData.callSid
  );
  const nextCall = {
    ...existingCall,
    ...callData,
  };

  if (callData.isMuted === undefined) {
    nextCall.isMuted = existingCall?.isMuted || false;
  }

  if (callData.isActive === undefined) {
    nextCall.isActive =
      existingCall?.isActive ||
      ['calling', 'in_call'].includes(nextCall.status);
  }

  if (nextCall.status === 'ringing' && nextCall.callDirection === 'inbound') {
    nextCall.isActive = false;
  }

  if (nextCall.status === 'in_call' && !nextCall.connectedAt) {
    nextCall.connectedAt = Date.now();
  }

  if (nextCall.status !== 'in_call' && callData.connectedAt === undefined) {
    nextCall.connectedAt = existingCall?.connectedAt || null;
  }

  if (existingCall) {
    calls.value = calls.value.map(call =>
      call.callSid === callData.callSid ? nextCall : call
    );
  } else {
    calls.value.push(nextCall);
  }

  maybeNavigatePendingCall(nextCall);
};

const removeCall = callSid => {
  calls.value = calls.value.filter(call => call.callSid !== callSid);

  if (pendingNavigationCallSid.value === callSid) {
    pendingNavigationCallSid.value = null;
  }
};

const getSubtitle = call => {
  if (!call) {
    return '';
  }

  if (call.status === 'in_call') {
    return formatDuration(call.connectedAt);
  }

  if (call.callDirection === 'outbound') {
    return t('CONVERSATION.VOICE_WIDGET.OUTGOING_CALL');
  }

  return t('CONVERSATION.VOICE_WIDGET.INCOMING_CALL');
};

const handleCallState = event => {
  upsertCall(event?.detail || {});
};

const handleIncomingCall = event => {
  const detail = event?.detail || {};
  upsertCall({
    ...detail,
    status: 'ringing',
    isActive: false,
    isMuted: false,
  });
};

const handleCallCreated = event => {
  upsertCall(event?.detail || {});
};

const handleCallDisconnected = event => {
  removeCall(event?.detail?.callSid);
};

const handleCallMediaState = event => {
  const detail = event?.detail || {};
  upsertCall({
    callSid: detail.callSid,
    inboxId: detail.inboxId,
    isMuted: detail.isMuted,
  });
};

const seedCurrentCalls = () => {
  const snapshots = sipConnectionManager.getCallSnapshots?.() || [];
  snapshots.forEach(snapshot => upsertCall(snapshot));
};

const answerIncomingCall = async call => {
  const { inbox } = getCallInfo(call);
  if (!call || !inbox) {
    return;
  }

  try {
    pendingNavigationCallSid.value = call.callSid;
    await sipCallAdapter.joinCall({
      inbox,
      callSid: call.callSid,
      callDirection: call.callDirection,
    });

    if (call.conversationId) {
      pendingNavigationCallSid.value = null;
      navigateToConversation(call.conversationId, call.inboxId);
    }
  } catch (error) {
    pendingNavigationCallSid.value = null;
    useAlert(error?.message || 'Failed to answer SIP call');
  }
};

const rejectIncomingCall = async call => {
  const { inbox } = getCallInfo(call);
  if (!call || !inbox) {
    return;
  }

  try {
    await sipCallAdapter.rejectIncomingCall({
      inbox,
      callSid: call.callSid,
    });
    removeCall(call.callSid);
  } catch (error) {
    useAlert(error?.message || 'Failed to reject SIP call');
  }
};

const endActiveCall = async call => {
  const { inbox } = getCallInfo(call);
  if (!call || !inbox) {
    return;
  }

  try {
    await sipCallAdapter.endCall({
      inbox,
      callSid: call.callSid,
    });
  } catch (error) {
    useAlert(error?.message || 'Failed to end SIP call');
  }
};

const toggleMute = async call => {
  const { inbox } = getCallInfo(call);
  if (!call || !inbox) {
    return;
  }

  try {
    const isMuted = await sipCallAdapter.toggleMute({
      inbox,
      callSid: call.callSid,
    });

    upsertCall({
      callSid: call.callSid,
      inboxId: call.inboxId,
      isMuted,
    });
  } catch (error) {
    useAlert(error?.message || 'Failed to update microphone state');
  }
};

onMounted(() => {
  seedCurrentCalls();
  window.addEventListener('sip:call-state', handleCallState);
  window.addEventListener('sip:incoming-call', handleIncomingCall);
  window.addEventListener('sip:call-created', handleCallCreated);
  window.addEventListener('sip:call-disconnected', handleCallDisconnected);
  window.addEventListener('sip:call-media-state', handleCallMediaState);
});

onUnmounted(() => {
  stopDurationTimer();
  window.removeEventListener('sip:call-state', handleCallState);
  window.removeEventListener('sip:incoming-call', handleIncomingCall);
  window.removeEventListener('sip:call-created', handleCallCreated);
  window.removeEventListener('sip:call-disconnected', handleCallDisconnected);
  window.removeEventListener('sip:call-media-state', handleCallMediaState);
});
</script>

<template>
  <div
    v-show="hasCalls"
    class="fixed ltr:right-4 rtl:left-4 bottom-4 z-50 flex flex-col gap-2 w-80"
  >
    <div
      v-for="call in hasActiveCall ? incomingCalls : []"
      :key="call.callSid"
      class="flex items-center gap-3 p-4 bg-n-solid-2 rounded-xl shadow-xl outline outline-1 outline-n-strong"
    >
      <div class="animate-pulse ring-2 ring-n-teal-9 rounded-full inline-flex">
        <Avatar
          :src="getCallInfo(call).avatar"
          :name="getCallInfo(call).contactName"
          :size="40"
          rounded-full
        />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-n-slate-12 truncate mb-0">
          {{ getCallInfo(call).contactName }}
        </p>
        <p class="text-xs text-n-slate-11 truncate">
          {{ getCallInfo(call).inboxName }}
        </p>
      </div>
      <div class="flex shrink-0 gap-2">
        <button
          class="flex justify-center items-center w-10 h-10 bg-n-ruby-9 hover:bg-n-ruby-10 rounded-full transition-colors"
          @click="rejectIncomingCall(call)"
        >
          <i class="text-lg text-white i-ph-phone-x-bold" />
        </button>
        <button
          class="flex justify-center items-center w-10 h-10 bg-n-teal-9 hover:bg-n-teal-10 rounded-full transition-colors"
          @click="answerIncomingCall(call)"
        >
          <i class="text-lg text-white i-ph-phone-bold" />
        </button>
      </div>
    </div>

    <div
      v-if="primaryCall"
      class="flex items-center gap-3 p-4 bg-n-solid-2 rounded-xl shadow-xl outline outline-1 outline-n-strong"
    >
      <div
        class="ring-2 ring-n-teal-9 rounded-full inline-flex"
        :class="{ 'animate-pulse': !hasActiveCall }"
      >
        <Avatar
          :src="getCallInfo(primaryCall).avatar"
          :name="getCallInfo(primaryCall).contactName"
          :size="40"
          rounded-full
        />
      </div>
      <button
        type="button"
        class="flex-1 min-w-0 text-left"
        @click="
          navigateToConversation(
            primaryCall.conversationId,
            primaryCall.inboxId
          )
        "
      >
        <p class="text-sm font-medium text-n-slate-12 truncate mb-0">
          {{ getCallInfo(primaryCall).contactName }}
        </p>
        <p
          v-if="primaryCall.status === 'in_call'"
          class="font-mono text-sm text-n-teal-9"
        >
          {{ getSubtitle(primaryCall) }}
        </p>
        <p v-else class="text-xs text-n-slate-11">
          {{ getSubtitle(primaryCall) }}
        </p>
        <p
          v-if="hasActiveCall && primaryCall.isMuted"
          class="text-xs text-n-amber-11 mt-1"
        >
          {{ t('SIP_CHANNEL.CALL_WIDGET.MIC_MUTED') }}
        </p>
      </button>
      <div class="flex shrink-0 gap-2">
        <button
          v-if="hasActiveCall"
          class="flex justify-center items-center w-10 h-10 rounded-full transition-colors"
          :class="[
            primaryCall.isMuted
              ? 'bg-n-amber-9 hover:bg-n-amber-10'
              : 'bg-n-slate-9 hover:bg-n-slate-10',
          ]"
          :title="
            primaryCall.isMuted
              ? t('SIP_CHANNEL.CALL_WIDGET.UNMUTE')
              : t('SIP_CHANNEL.CALL_WIDGET.MUTE')
          "
          @click="toggleMute(primaryCall)"
        >
          <i
            :class="
              primaryCall.isMuted
                ? 'i-ph-microphone-slash-fill'
                : 'i-ph-microphone-fill'
            "
            class="text-lg text-white"
          />
        </button>
        <button
          class="flex justify-center items-center w-10 h-10 bg-n-ruby-9 hover:bg-n-ruby-10 rounded-full transition-colors"
          @click="
            hasActiveCall
              ? endActiveCall(primaryCall)
              : rejectIncomingCall(primaryCall)
          "
        >
          <i class="text-lg text-white i-ph-phone-x-bold" />
        </button>
        <button
          v-if="!hasActiveCall"
          class="flex justify-center items-center w-10 h-10 bg-n-teal-9 hover:bg-n-teal-10 rounded-full transition-colors"
          @click="answerIncomingCall(primaryCall)"
        >
          <i class="text-lg text-white i-ph-phone-bold" />
        </button>
      </div>
    </div>
  </div>
</template>
